import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import ReactDatePicker, { setDefaultLocale, ReactDatePickerProps, registerLocale } from 'react-datepicker';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { enGB, fr } from 'date-fns/locale';
import { ArrowIcon } from '../icons';
import { Input } from '../atomic-components';
import { setToEndOfDay, setToStartOfDay, useLanguage } from '@algoan/dashboard/utils';
import { isAfterStartDate, isAfterEndDate, isInRange } from './date-range-picker-utils';

registerLocale('en-GB', enGB);
registerLocale('fr', fr);

export interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChangeStartDate: (date: Date | null) => void;
  onChangeEndDate: (date: Date | null) => void;
  maxDate?: Date;
  minDate?: Date;
  className?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChangeStartDate,
  onChangeEndDate,
  maxDate,
  minDate,
  className,
}) => {
  const { language } = useLanguage();

  useEffect(() => {
    setDefaultLocale(language);
  }, [language]);

  const sharedProps: Partial<ReactDatePickerProps> = {
    renderCustomHeader,
    minDate: minDate,
    maxDate: maxDate,
    startDate: startDate,
    endDate: endDate,
    showPopperArrow: false,
    locale: language,
    dateFormat: 'dd/MM/yyyy',
    formatWeekDay: (date) => date[0], // display only the first letter of the day
    disabledKeyboardNavigation: true,
    calendarStartDay: 1,
  };

  return (
    <DateRangePickerStyles.Container className={className}>
      <DateRangePickerStyles.DateContainer>
        <DateRangePickerStyles.Title>
          <FormattedMessage
            id="date_picker.from_date_title"
            defaultMessage="Start Date"
            description="Label of start date title"
          />
        </DateRangePickerStyles.Title>
        <DateRangePickerStyles.InnerContainer>
          {/* we use 2 inline date pickers for the date picker UI, and 2
     non-inline hidden date pickers for the start and end date input fields. */}
          <DateRangePickerStyles.DatePickerContainerStartDate>
            <ReactDatePicker
              selected={startDate}
              filterDate={(date: Date | null) => isInRange({ date, minDate, maxDate })}
              inline={true}
              onChange={(date: Date | null) => {
                if (isAfterEndDate(date, endDate)) {
                  onChangeEndDate(null);
                }
                onChangeStartDate(date !== null ? setToStartOfDay(date) : null);
              }}
              {...sharedProps}
            />
          </DateRangePickerStyles.DatePickerContainerStartDate>
          {/* we only use this react date picker for the start date input field */}
          <DateRangePickerStyles.HiddenReactDatePickerContainer>
            <ReactDatePicker
              selected={startDate}
              customInput={<FromDateInput />}
              filterDate={(date: Date | null) => isInRange({ date, minDate, maxDate })}
              onChange={(date: Date | null) => {
                if (isAfterEndDate(date, endDate)) {
                  onChangeEndDate(null);
                }
                onChangeStartDate(date !== null ? setToStartOfDay(date) : null);
              }}
              {...sharedProps}
            />
          </DateRangePickerStyles.HiddenReactDatePickerContainer>
        </DateRangePickerStyles.InnerContainer>
      </DateRangePickerStyles.DateContainer>
      <DateRangePickerStyles.DateContainer>
        <DateRangePickerStyles.Title>
          <FormattedMessage
            id="date_picker.to_date_title"
            defaultMessage="End Date"
            description="Label of end date title"
          />
        </DateRangePickerStyles.Title>
        <DateRangePickerStyles.InnerContainer>
          <DateRangePickerStyles.DatePickerContainerEndDate>
            <ReactDatePicker
              selected={endDate}
              inline={true}
              onChange={(date: Date | null) => {
                onChangeEndDate(date !== null ? setToEndOfDay(date) : null);
              }}
              filterDate={(date: Date | null) =>
                isInRange({ date, minDate, maxDate }) && isAfterStartDate(date, startDate)
              }
              {...sharedProps}
            />
          </DateRangePickerStyles.DatePickerContainerEndDate>
          {/* we only use this react date picker for the end date input field */}
          <DateRangePickerStyles.HiddenReactDatePickerContainer>
            <ReactDatePicker
              selected={endDate}
              customInput={<ToDateInput />}
              onChange={(date: Date | null) => {
                onChangeEndDate(date !== null ? setToEndOfDay(date) : null);
              }}
              filterDate={(date: Date | null) =>
                isInRange({ date, minDate, maxDate }) && isAfterStartDate(date, startDate)
              }
              {...sharedProps}
            />
          </DateRangePickerStyles.HiddenReactDatePickerContainer>
        </DateRangePickerStyles.InnerContainer>
      </DateRangePickerStyles.DateContainer>
    </DateRangePickerStyles.Container>
  );
};

const FromDateInput: FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
  return (
    <>
      <DateRangePickerStyles.InputContainer>
        <DateRangePickerStyles.Text>
          <FormattedMessage id="date_picker.from_date" defaultMessage="From" description="Label of from date input" />
        </DateRangePickerStyles.Text>
        <DateRangePickerStyles.StyledInput {...props} />
      </DateRangePickerStyles.InputContainer>
      <DateRangePickerStyles.Divider />
    </>
  );
};

const ToDateInput: FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
  return (
    <>
      <DateRangePickerStyles.InputContainer>
        <DateRangePickerStyles.Text>
          <FormattedMessage id="date_picker.to_date" defaultMessage="To" description="Label of to date input" />
        </DateRangePickerStyles.Text>
        <DateRangePickerStyles.StyledInput {...props} />
      </DateRangePickerStyles.InputContainer>
      <DateRangePickerStyles.Divider />
    </>
  );
};

const renderCustomHeader: ReactDatePickerProps['renderCustomHeader'] = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => (
  <DateRangePickerStyles.Header>
    <FormattedDate value={date} month="long" year="numeric" />
    <DateRangePickerStyles.ButtonContainer>
      <DateRangePickerStyles.Arrow
        size="large"
        direction="left"
        onClick={prevMonthButtonDisabled ? undefined : decreaseMonth}
        disabled={prevMonthButtonDisabled}
      />
      <DateRangePickerStyles.Arrow
        size="large"
        direction="right"
        onClick={nextMonthButtonDisabled ? undefined : increaseMonth}
        disabled={nextMonthButtonDisabled}
      />
    </DateRangePickerStyles.ButtonContainer>
  </DateRangePickerStyles.Header>
);

export const DateRangePickerStyles = {
  Container: styled.div`
    display: flex;
    justify-content: space-around;
  `,
  Title: styled.div`
    flex: 1;
    text-align: center;
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    text-transform: uppercase;
    color: #00466b;
    margin-bottom: 16px;
  `,
  DateContainer: styled.div`
    width: 333px;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  InnerContainer: styled.div`
    user-select: none;
    display: flex;
    position: relative;
    height: 420px;
    width: 333px;
    justify-content: space-between;

    .react-datepicker {
      border: 1px solid #e0e8ec;
      padding: 15px;
      padding-top: 45px;
      height: 350px;
      border-radius: 6px;
    }
    .react-datepicker-popper {
      position: static !important; // tempoary until put into a modal
      transform: none !important; // tempoary until put into a modal
    }
    .react-datepicker__day {
      margin: 0;
      border-top: 1px solid #e0e8ec;
      border-left: 1px solid #e0e8ec;
      width: 40px;
      height: 40px;
      line-height: 40px;
      outline: none;
      color: ${(props) => props.theme.algoanBlue};

      &:hover {
        border-radius: 0;
        background-color: #e0e8ec;
      }
    }

    .react-datepicker__day--today {
      background: white;
      color: ${(props) => props.theme.algoanBlue};
      font-weight: normal;

      background-image: url(/images/elipse.png);
      background-size: 35px 35px;
      background-repeat: no-repeat;
      background-position: center;

      &:hover {
        background-color: #e0e8ec;
      }
    }
    .react-datepicker__day--in-range {
      background: #ecf1f4;
      border-radius: 0;
      color: ${(props) => props.theme.algoanBlue};

      &:hover {
        background-color: #ccd9e1;
      }
    }
    .react-datepicker__day-name {
      width: 37px;
      text-transform: uppercase;
      color: ${(props) => props.theme.algoanBlue};
    }
    .react-datepicker__header {
      background-color: white;
      border-bottom: none;
      color: ${(props) => props.theme.algoanBlue};
    }
    .react-datepicker__month {
      border-right: 1px solid #e0e8ec;
      border-bottom: 1px solid #e0e8ec;
    }
    .react-datepicker__day--disabled {
      color: #7fa2b5;
      background: rgba(245, 248, 250, 0.6);

      &:hover {
        background: rgba(245, 248, 250, 0.6);
      }
    }

    .react-datepicker__day--selected {
      background: ${(props) => props.theme.algoanBlue};
      color: white;
      border-radius: 0;

      &:hover {
        background-color: ${(props) => props.theme.algoanBlue};
      }
    }
  `,
  HiddenReactDatePickerContainer: styled.div`
    position: absolute;
    left: 51px;
    margin-top: 15px;

    .react-datepicker {
      display: none;
    }
  `,

  Header: styled.div`
    display: flex;
    justify-content: space-between;
    margin-left: 8px;
    margin-right: 8px;
    margin-bottom: 8px;
    margin-top: 12px;
    height: 30px;
    line-height: 30px;
    background-color: white;
    text-transform: capitalize;
    font-size: 13px;
    font-weight: bold;
  `,
  ButtonContainer: styled.div`
    display: flex;
    justify-content: space-between;
    width: 50px;
  `,
  Arrow: styled(ArrowIcon)<{ disabled?: boolean }>`
    background-size: cover;
    margin: auto 0;
    border: none;
    outline: none;
    background-color: white;
    padding: 0;
    filter: ${(props) => (props.disabled ? 'opacity(45%)' : '')};
    cursor: ${(props) => (props.disabled ? 'select' : 'pointer')};
  `,
  Text: styled.div`
    padding-right: 16px;
    font-size: 11px;
    color: ${(props) => props.theme.algoanBlue};
    margin: auto 0;
  `,
  InputContainer: styled.div`
    display: flex;
  `,
  StyledInput: styled(Input)`
    input {
      height: 30px;
      font-size: 11px;
      width: 207px;
    }
  `,
  DatePickerContainerStartDate: styled.div`
    .react-datepicker__day--range-end {
      background: #7fa2b5;
      color: white;
      border-radius: 0;
      font-weight: bold;

      &:hover {
        background-color: #7fa2b5;
      }
    }

    .react-datepicker__day--range-start {
      background: ${(props) => props.theme.algoanBlue};
      color: white;
      border-radius: 0;
      font-weight: bold;

      &:hover {
        background-color: ${(props) => props.theme.algoanBlue};
      }
    }
  `,
  DatePickerContainerEndDate: styled.div`
    .react-datepicker__day--range-start {
      background: #7fa2b5;
      color: white;
      border-radius: 0;
      font-weight: bold;

      &:hover {
        background-color: #7fa2b5;
      }
    }

    .react-datepicker__day--range-end {
      background: ${(props) => props.theme.algoanBlue};
      color: white;
      border-radius: 0;
      font-weight: bold;

      &:hover {
        background-color: ${(props) => props.theme.algoanBlue};
      }
    }
  `,
  Divider: styled.div`
    border-bottom: 1px solid #e0e8ec;
    margin-left: -30px;
    margin-right: -21px;
    margin-top: 12px;

    &:before {
      content: '';
      position: absolute;
      left: 108px;
      height: 10px;
      width: 10px;
      top: 37px;
      border-radius: 0 0 2px 0;
      background: white;
      transform: rotate(45deg);
      border-bottom: inherit;
      border-right: 1px solid #e0e8ec;
      box-shadow: inherit;
    }
  `,
};
